/**
 * Shared Authentication Types
 * 
 * These types are provider-agnostic and used across all authentication implementations.
 * They define the core data structures for authentication without coupling to any specific provider.
 */

/**
 * Represents an authenticated user session
 */
export interface AuthSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  expires_in: number;
  token_type: string;
  user: AuthUser;
}

/**
 * Represents a basic authenticated user (provider-agnostic)
 */
export interface AuthUser {
  id: string;
  email: string;
  email_confirmed_at?: string;
  created_at: string;
  updated_at?: string;
  user_metadata?: Record<string, any>;
}

/**
 * Result of an authentication operation
 */
export interface AuthResult {
  user: AuthUser | null;
  session: AuthSession | null;
}

/**
 * Tenant information
 */
export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status?: string;
  settings?: {
    landing_page?: string;
    [key: string]: any;
  };
}

/**
 * User profile with tenant context
 */
export interface UserWithTenant {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  phone?: string;
  is_active: boolean;
  tenant: TenantInfo;
  roles: string[];
}

/**
 * Complete authentication response with tenant context
 */
export interface TenantAuthResponse {
  user: UserWithTenant;
  session: AuthSession;
  authUser: AuthUser;
}

/**
 * Sign up data for tenant-scoped registration
 */
export interface TenantSignUpData {
  email: string;
  password: string;
  name: string;
  tenant_id: string;
  phone?: string;
}

/**
 * Callback for authentication state changes
 */
export type AuthStateChangeCallback = (
  event: AuthStateChangeEvent,
  session: AuthSession | null
) => void;

/**
 * Authentication state change events
 */
export type AuthStateChangeEvent =
  | 'SIGNED_IN'
  | 'SIGNED_OUT'
  | 'TOKEN_REFRESHED'
  | 'USER_UPDATED'
  | 'PASSWORD_RECOVERY';

/**
 * Authentication subscription for cleanup
 */
export interface AuthSubscription {
  unsubscribe: () => void;
}

/**
 * Error thrown by authentication operations
 */
export class AuthError extends Error {
  code?: string;
  status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
  }
}

/** Raised when the user's organisation (tenant) has been deactivated by the super-admin. */
export const TENANT_DEACTIVATED_CODE = 'TENANT_DEACTIVATED';
export const TENANT_DEACTIVATED_MESSAGE =
  "Your organisation's access has been deactivated. Contact your administrator.";

/** Raised when the user's own account has been deactivated in User Management. */
export const USER_DEACTIVATED_CODE = 'USER_DEACTIVATED';
export const USER_DEACTIVATED_MESSAGE =
  'Your account has been deactivated. Contact your administrator.';

